import React, { useState, useEffect } from "react";
// currency utilities
// currency utilities using new hook-based helper
import { useCurrencyUtils } from "../utils/currencyUtils";
import { bidIcon } from "../assets/VehicleDetailsIcon";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import api from "../lib/api.ts";
import { useToast } from "../context/ToastContext";

import {
  FaGavel,
  FaMapMarkerAlt,
  FaRegHeart,
  FaShareAlt,
  FaPlus,
  FaMinus,
  FaHammer,
  FaTwitter,
  FaFacebookF,
  FaWhatsapp,
  FaLink,
} from "react-icons/fa";
const SectionHeader = ({ title, icon }) => (
  <h2 className="bg-primary-soft text-primary p-[10px] m-[-15px] mb-[10px] border-b-0 font-bold flex items-center gap-1 text-lg md:text-[18px]">
    <span className="text-primary">{icon}</span>
    {title}
  </h2>
);

const BidInformation = ({
  vehicle,
  prebidTimeLeft = 0,
  bidAmount = "",
  setBidAmount = () => { },
  bidIncrement = vehicle?.bidIncrement,
  bidMessage = "",
  isSubmittingBid = false,
  handlePreBidSubmit = () => { },
  handleBuyNowSubmit = () => { },
  getMinBidAmount = () => 0,
  className = "",
}) => {

  // console.log('bid information props ', vehicle)
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  // currency context not needed directly in most cases
  // const { currency } = useCurrency();
  const { displayPriceWithSymbol, convertPrice } = useCurrencyUtils();
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const shareRef = React.useRef(null);
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { showSuccess, showError } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const shareUrl = window.location.href;
  const shareText = vehicle?.name || "Check this vehicle";

  const bidIncrementValue = parseFloat(bidIncrement) || 0;

  //  console.log("vehicle data from the bid information" , vehicle);

  // Ensure bidAmount prop is initialized if empty
  useEffect(() => {
    if (!bidAmount) {
      setBidAmount(minBidAmount.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.latestBid, vehicle?.startingBid, bidIncrement]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        // Use the action from API response to determine the new state
        const action = response.data.data?.action;
        const isAdded = action === "added";
        setIsBookmarked(isAdded);

        // Show appropriate toast message based on API response
        if (isAdded) {
          showSuccess("Vehicle added to Watch List successfully!");
        } else {
          showSuccess("Vehicle removed from Watch List successfully!");
        }
      } else {
        // Show the specific API error message
        const errorMessage =
          response.data.message ||
          "Failed to update Watch List. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      // Show specific error message from API response if available
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update Watch List. Please try again.";
      showError(errorMessage);
    }
  };

  const handleShare = async (platform) => {
    // Native mobile share (best UX)
    if (platform === "native" && navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
      return;
    }

    let url = "";

    switch (platform) {
      case "copy":
        navigator.clipboard.writeText(shareUrl);
        alert("Link copied to clipboard!");
        break;

      case "twitter":
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          shareUrl,
        )}&text=${encodeURIComponent(shareText)}`;
        break;

      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl,
        )}`;
        break;

      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + shareUrl,
        )}`;
        break;

      default:
        return;
    }

    if (url) window.open(url, "_blank");

    setShowShareDropdown(false);
  };

  // Buy now timer state
  const [buyNowTimeLeft, setBuyNowTimeLeft] = useState(() => {
    if (!vehicle?.buyNowEndTime) return 0;
    const endTime = new Date(vehicle.buyNowEndTime);
    const now = new Date();
    return Math.max(0, endTime.getTime() - now.getTime());
  });

  // Track if buy now purchase was successful
  const [isPurchaseSuccessful, setIsPurchaseSuccessful] = useState(false);

  // State for Buy Now confirmation modal
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  // Buy now timer effect
  useEffect(() => {
    if (!vehicle?.buyNowEndTime || buyNowTimeLeft <= 0) return;

    const timer = setInterval(() => {
      const endTime = new Date(vehicle.buyNowEndTime);
      const now = new Date();
      const timeLeft = Math.max(0, endTime.getTime() - now.getTime());
      setBuyNowTimeLeft(timeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [vehicle?.buyNowEndTime, buyNowTimeLeft]);

  // Effect to detect successful purchase
  useEffect(() => {
    if (
      bidMessage &&
      bidMessage.includes("successfully") &&
      bidMessage.includes("Purchase")
    ) {
      setIsPurchaseSuccessful(true);
    }
  }, [bidMessage]);

  // Handle Buy Now modal close
  const handleBuyNowModalClose = () => {
    setShowBuyNowModal(false);
  };

  // Handle Buy Now button click - show confirmation modal
  const handleBuyNowClick = () => {
    if (!user?.id) {
      alert(translate("bidInfo.alerts.loginToPurchase"));
      return;
    }
    if (user?.seller?.id) {
      alert(translate("bidInfo.alerts.sellerCannotPurchase"));
      return;
    }
    if (!hasActivePackage) {
      alert(translate("bidInfo.alerts.needActivePackagePurchase"));
      return;
    }

    // Show Buy Now confirmation modal
    setShowBuyNowModal(true);
  };

  const onPreBidClick = () => {
    setErrorMessage(""); // clear previous errors
    setShowConfirmation(true); // show modal
  };
  const confirmBid = async () => {
    
    try {
      setShowConfirmation(false); // close modal
      await handlePreBidSubmit(); // call your prop function
    } catch (error) {
      setShowConfirmation(true); // keep modal open if error
      setErrorMessage(error?.message || "Something went wrong!");
    }
  };

  const cancelBid = () => {
    setShowConfirmation(false);
    setErrorMessage("");
    showError("Your Bid was cancelled");
  };

  // Check if vehicle is not yet assigned to any auction lane (future)
  // If auctionLanesVehicles is empty or undefined, it's a future vehicle
  const isFuture =
    !Array.isArray(vehicle?.auctionLanesVehicles) ||
    vehicle?.auctionLanesVehicles?.length === 0;

  // Check if this is a buy now vehicle
  const isBuyNowVehicle =
    vehicle?.ProductStatus?.name === "Buy Now" ||
    vehicle?.ProductStatus === "Buy Now" ||
    (vehicle?.buyNowPrice && vehicle?.buyNowPrice > 0);

  // Check if vehicle is already sold
  const isVehicleSold =
    vehicle?.ProductStatus?.name === "Sold" ||
    vehicle?.ProductStatus === "Sold" ||
    vehicle?.soldPrice ||
    vehicle?.soldTo ||
    vehicle?.ProductAuctionStatus?.name === "Sold" ||
    vehicle?.ProductAuctionStatus === "Sold";

  // Check buyer eligibility based on buyerLimit structure
  const buyerLimit = user?.buyer?.buyerLimit;
  const hasActivePackage =
    buyerLimit &&
    (buyerLimit.remainingCreditLimit > 0 || buyerLimit.remainingCarLimit > 0);
  const remainingCreditLimit = buyerLimit?.remainingCreditLimit || 0;
  const remainingCarLimit = buyerLimit?.remainingCarLimit || 0;
  const currentBidAmount =
    parseFloat(vehicle?.latestBid) || parseFloat(vehicle?.startingBid) || 0;

  const isEligible =
    hasActivePackage &&
    remainingCarLimit > 0 &&
    remainingCreditLimit >= currentBidAmount;

  // Default bid info values
  const bidInfo = {
    eligibilityStatus: isEligible
      ? translate("bidInfo.status.eligible")
      : translate("bidInfo.status.notEligible"),
    startingBid: vehicle?.startingBid || vehicle?.startBidding || 0,
    currentBid: vehicle?.latestBid || 0,
    yourBidStatus: translate("bidInfo.status.notBidYet"),
    marketValue: vehicle?.marketValue || 0,
  };
  const rowClass = isRTL ? "flex-row-reverse text-right" : "";

  // Logic for min bid
  const currentBid =
    parseFloat(vehicle?.latestBid) || parseFloat(vehicle?.startingBid) || 0;
  const minBidAmount = currentBid + bidIncrement;
  useEffect(() => {
    if (!bidAmount) {
      setBidAmount(minBidAmount.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.latestBid, vehicle?.startingBid, bidIncrement]);

  // const updateAmount = (type) => {
  //   const current = parseFloat(bidAmount) || minBidAmount;
  //   const nextValue =
  //     type === "plus"
  //       ? current + bidIncrement
  //       : Math.max(minBidAmount, current - bidIncrement);
  //   setBidAmount(nextValue.toString());
  // };
  const updateAmount = (type) => {
    const current = parseFloat(bidAmount) || minBidAmount;
    const nextValue =
      type === "plus"
        ? current + bidIncrementValue
        : Math.max(minBidAmount, current - bidIncrementValue);
    setBidAmount(nextValue.toString());
  };
  // 1. Extract the deep location data here
  // Try to find location from various possible paths
  const location =
    vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.location?.name ||
    vehicle?.location ||
    vehicle?.saleLocation ||
    "Dubai , UAE";

  return (
    <div
      className={`bg-card p-5 md:p-6 rounded-2xl shadow-xl border border-default ${className}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* 1. Badge & Lot Number Row */}
      <div className="flex justify-between items-center gap-4 mb-3 w-full">
        {/* FIXED BADGE: Added flex-shrink-0 and whitespace-nowrap to prevent stretching */}
        <div className="flex-shrink-0 bg-primary text-white text-[10px] px-2 py-0 rounded-[4px] flex items-center gap-1 font-bold uppercase whitespace-nowrap shadow-sm">
          {translate("vehicleDetail.verifiedListing")}
        </div>

        {/* FLEXIBLE LOT NUMBER: Truncates text, shows full on hover without shifting layout */}
        <div className="relative group flex-1 flex justify-end min-w-0">
          <span className="text-description text-[12px] font-mono truncate cursor-default">
            {translate("vehicleDetail.lot")} {vehicle?.lotNumber || "N/A"}
          </span>

          {/* HOVER TOOLTIP: Appears only when data is long */}
          <div className="absolute bottom-full mb-1 hidden group-hover:block z-50 bg-gray-800 text-white text-[12px] py-1 px-2 rounded shadow-xl whitespace-nowrap">
            {translate("vehicleDetail.lot")} {vehicle?.lotNumber}
          </div>
        </div>
      </div>

      {/* 2. Vehicle Title */}
      <h1 className="text-white text-[24px] font-bold mb-1">
        {vehicle?.name || "Vehicle Title"}
      </h1>


      {/* 3. Location + Market Value */}
      <div className="flex justify-between items-center gap-1 text-description text-[14px] mb-4">
        {/* Left: Location */}
        <span className="text-description">{location}</span>

        {/* Right: Market Value */}
        <span className="text-description font-bold">
          {translate("vehicleDetail.marketValue")}: {displayPriceWithSymbol(vehicle?.marketValue || 0)}
        </span>
      </div>

      <hr className="border-default mb-4" />

      {/* 4. Bidding Info Row */}
      <div className="flex justify-between items-end mb-5">
        <div className="flex flex-col">
          <span className="text-description text-[10px] uppercase tracking-wider">
            {translate("bidInfo.labels.currentBid")}
          </span>
          <span className="text-primary text-2xl font-bold">
            {displayPriceWithSymbol(convertPrice(currentBid))}
          </span>
        </div>

        <div className="flex flex-col items-end">
          <span className="text-description text-[10px] uppercase tracking-wider">
            {translate("vehicleDetail.endsin")}
          </span>
          <span className="text-white text-[14px] font-bold">
            {(() => {
              const timeToDisplay = isBuyNowVehicle
                ? buyNowTimeLeft
                : prebidTimeLeft;

              // If time is negative or zero, we set it to 0 so the math below results in "00"
              const time = Math.max(0, timeToDisplay);

              const days = Math.floor(time / (1000 * 60 * 60 * 24));
              const hours = Math.floor(
                (time % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
              );
              const minutes = Math.floor(
                (time % (1000 * 60 * 60)) / (1000 * 60),
              );
              const seconds = Math.floor((time % (1000 * 60)) / 1000);

              // Returns 00d : 00h : 00m even if time is up
              return `${String(days).padStart(2, "0")}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
            })()}
          </span>
        </div>
      </div>

      <hr className="border-default mb-5" />

      {/* 5. Price Controller Row */}
      <div className="flex items-center gap-2 mb-4">
        {/* Minus Button: Styled with the Price's original large padding */}
        <button
          onClick={() => updateAmount("minus")}
          className="bg-soft text-white py-3.5 px-3 rounded-lg border border-default hover:opacity-80 transition-all flex justify-center items-center"
        >
          <FaMinus size={14} />
        </button>

        {/* Price Display: Styled with the Button's original compact padding & 14px size */}
        <div className="flex-1 bg-black text-white text-center p-1  rounded-lg font-bold text-[22px] border-2 border-default">
          {/* {displayPriceWithSymbol(convertPrice(bidAmount || minBidAmount))} */}
          {displayPriceWithSymbol(convertPrice(parseFloat(bidAmount)))}
        </div>

        {/* Plus Button: Styled with the Price's original large padding */}
        <button
          onClick={() => updateAmount("plus")}
          className="bg-[#1B2C39] text-white py-3.5 px-3 border border-default rounded-lg hover:opacity-80 transition-all flex justify-center items-center"
        >
          <FaPlus size={14} />
        </button>
      </div>

      <div className="flex justify-center mb-6">
        <button
          // onClick={handlePreBidSubmit}
          onClick={onPreBidClick} // previously handlePreBidSubmit
          disabled={isSubmittingBid}
          className="
      /* 1. Small Width */
      w-[50%] 
      
      /* 2. Custom Color #135E9F */
      bg-[#135E9F] hover:bg-[#135E9F]/90 text-white 
      py-3 rounded-[8px] font-bold 
      flex items-center justify-center gap-2 
      
      /* 3. Transitions & Interaction */
      transition-all duration-300 active:scale-95 
      disabled:opacity-50 disabled:cursor-not-allowed
      
      /* 4. DEFAULT SHADOW (Visible without hover) */
      /* Horizontal 0, Vertical 8px, Blur 20px, Spread -2px */
      shadow-[0_8px_20px_-2px_rgba(19,94,159,0.6)] 
      
      /* Optional: Slightly deeper shadow on hover */
      hover:shadow-[0_12px_25px_-2px_rgba(19,94,159,0.8)]
    "
        >
          <FaGavel className={isRTL ? "scale-x-[-1]" : ""} />
          {isSubmittingBid
            ? translate("vehicleDetail.processing")
            : translate("vehicleDetail.bidNow")}
        </button>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Your Bid</h2>
            <p className="mb-2">
              Bid Amount: ${bidAmount || getMinBidAmount()}
            </p>
            <p className="mb-4">{bidMessage}</p>

            {/* Show error message if any */}
            {errorMessage && (
              <p className="text-red-600 mb-4">{errorMessage}</p>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={confirmBid}
                className="bg-[#135E9F] text-white py-2 px-6 rounded font-bold hover:bg-[#135E9F]/90 transition"
              >
                Confirm
              </button>
              <button
                onClick={cancelBid}
                className="bg-gray-300 text-gray-700 py-2 px-6 rounded font-bold hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. Footer Actions */}
      <div className="flex justify-between gap-3">
        <button
          onClick={() => toggleWishlist(vehicle?.id)}
          className="flex-1 flex items-center justify-center gap-2 py-3 group"
        >
          <div className="bg-page p-2 rounded-full transition-colors">
            <FaRegHeart
              size={16}
              className={`transition-all duration-300 ${isBookmarked
                  ? "text-primary scale-110"
                  : "text-white group-hover:text-primary"
                }`}
            />
          </div>
          <span className="text-description text-sm">
            {translate("vehicleDetail.watchListing")}
          </span>
        </button>

        <div className="relative flex-1" ref={shareRef}>
          <button
            onClick={() => {
              if (navigator.share && window.innerWidth < 768) {
                handleShare("native");
              } else {
                setShowShareDropdown(!showShareDropdown);
              }
            }}
            className="w-full flex items-center justify-center gap-2 py-3 group"
          >
            <div className="bg-page p-2 rounded-full text-white group-hover:text-primary transition-colors">
              <FaShareAlt size={16} />
            </div>
            <span className="text-description text-sm">
              {translate("vehicleDetail.share")}
            </span>
          </button>

          {/* Animated Dropdown */}
          <div
            className={`absolute bottom-full mb-2 right-0 bg-card border border-default rounded-xl shadow-xl w-48 z-50 overflow-hidden transform transition-all duration-200 ${showShareDropdown
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-2 pointer-events-none"
              }`}
          >
            <button
              onClick={() => handleShare("copy")}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
            >
              <FaLink className="text-white" />
              <span className="text-description">Copy Link</span>
            </button>

            <button
              onClick={() => handleShare("twitter")}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
            >
              <FaTwitter className="text-sky-400" />
              <span className="text-description">Twitter</span>
            </button>

            <button
              onClick={() => handleShare("facebook")}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
            >
              <FaFacebookF className="text-blue-600" />
              <span className="text-description"> Facebook</span>
            </button>

            <button
              onClick={() => handleShare("whatsapp")}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-page text-sm transition-colors"
            >
              <FaWhatsapp className="text-green-500" />
              <span className="text-description"> WhatsApp</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Message */}
      {bidMessage && (
        <div className="mt-4 text-center text-xs p-2 rounded bg-soft text-description italic">
          {bidMessage}
        </div>
      )}
    </div>
  );
};

export default BidInformation;

//   return (
//     <div
//       className={`border p-4 shadow-md bg-white ${className}`}
//       dir={isRTL ? "rtl" : "ltr"}
//       lang={language}
//     >
//       <SectionHeader title={translate("bidInfo.title")} icon={bidIcon} />
//       <ul className='text-[14px] text-[#242424] font-normal flex flex-col gap-4'>
//         {!isBuyNowVehicle && (
//           <li className={`flex justify-between ${rowClass}`}>
//             <span className='font-semibold'>
//               {translate("bidInfo.labels.eligibilityStatus")}:
//             </span>
//             <div
//               className={`flex items-center gap-1 ${
//                 isRTL ? "flex-row-reverse" : ""
//               }`}
//             >
//               <span className={isEligible ? "text-green-600" : "text-red-600"}>
//                 {isEligible ? "✓" : "✗"}
//               </span>
//               <span
//                 className={
//                   isEligible
//                     ? "text-green-600 font-semibold"
//                     : "text-red-600 font-semibold"
//                 }
//               >
//                 {bidInfo.eligibilityStatus}
//               </span>
//             </div>
//           </li>
//         )}

//         {!isBuyNowVehicle && (
//           <li className={`flex justify-between ${rowClass}`}>
//             <span className='font-semibold'>
//               {translate("bidInfo.labels.marketValue")}:
//             </span>
//             <span className={isRTL ? "text-left" : ""}>
//               {formatPrice(vehicle?.marketValue)}
//             </span>
//           </li>
//         )}

//         {isBuyNowVehicle ? (
//           <>
//             <li className={`flex justify-between ${rowClass}`}>
//               <span className='font-semibold'>
//                 {translate("bidInfo.labels.buyNowPrice")}:
//               </span>
//               <span className='text-green-600 font-bold text-lg'>
//                 {formatPrice(vehicle?.buyNowPrice)}
//               </span>
//             </li>
//             {vehicle?.buyNowEndTime && (
//               <li className={`flex justify-between ${rowClass}`}>
//                 <span className='font-semibold'>
//                   {translate("bidInfo.labels.buyNowTime")}:
//                 </span>
//                 <span className='text-red-600 font-semibold text-left'>
//                   {buyNowTimeLeft > 0
//                     ? (() => {
//                         const days = String(
//                           Math.floor(buyNowTimeLeft / (1000 * 60 * 60 * 24))
//                         ).padStart(2, "0");
//                         const hours = String(
//                           Math.floor(
//                             (buyNowTimeLeft % (1000 * 60 * 60 * 24)) /
//                               (1000 * 60 * 60)
//                           )
//                         ).padStart(2, "0");
//                         const minutes = String(
//                           Math.floor(
//                             (buyNowTimeLeft % (1000 * 60 * 60)) / (1000 * 60)
//                           )
//                         ).padStart(2, "0");
//                         const seconds = String(
//                           Math.floor((buyNowTimeLeft % (1000 * 60)) / 1000)
//                         ).padStart(2, "0");
//                         return `${days} : ${hours} : ${minutes} : ${seconds}`;
//                       })()
//                     : translate("bidInfo.status.expired")}
//                 </span>
//               </li>
//             )}
//           </>
//         ) : (
//           <>
//             <li className={`flex justify-between ${rowClass}`}>
//               <span className='font-semibold'>
//                 {translate("bidInfo.labels.startingBid")}:
//               </span>
//               <span>
//                 {formatPrice(vehicle?.startingBid || vehicle?.startBidding)}
//               </span>
//             </li>
//             <li className={`flex justify-between ${rowClass}`}>
//               <span className='font-semibold'>
//                 {translate("bidInfo.labels.currentBid")}:
//               </span>
//               <span id='current-bid' className={isRTL ? "text-left" : ""}>
//                 {formatPrice(vehicle?.latestBid) || "-"}
//               </span>
//             </li>
//           </>
//         )}
//         {/* Hide Your Bid Status for Buy Now vehicles and sold vehicles */}
//         {!isBuyNowVehicle &&
//           !(
//             vehicle?.ProductStatus && vehicle?.ProductStatus.name === "Sold"
//           ) && (
//             <li className={`flex justify-between ${rowClass}`}>
//               <span className='font-semibold'>
//                 {translate("bidInfo.labels.yourBidStatus")}:
//               </span>
//               <span
//                 className={
//                   !user?.id
//                     ? "text-primary font-semibold"
//                     : user?.userType === "seller"
//                     ? "text-primary font-semibold"
//                     : user?.userType === "buyer" &&
//                       vehicle?.latestBid &&
//                       vehicle?.highestBidderId === user?.id
//                     ? "text-green-600 font-semibold"
//                     : user?.userType === "buyer" &&
//                       vehicle?.latestBid &&
//                       user?.id
//                     ? "text-red-600 font-semibold"
//                     : user?.userType === "buyer"
//                     ? "text-primary font-semibold"
//                     : "text-primaryfont-semibold"
//                 }
//               >
//                 {!user?.id
//                   ? translate("bidInfo.status.loginAsBuyer")
//                   : user?.userType === "seller"
//                   ? translate("bidInfo.status.loginAsBuyer")
//                   : user?.userType === "buyer" &&
//                     vehicle?.latestBid &&
//                     vehicle?.highestBidderId === user?.id
//                   ? translate("bidInfo.status.winningBid")
//                   : user?.userType === "buyer" && vehicle?.latestBid && user?.id
//                   ? translate("bidInfo.status.losingBid")
//                   : user?.userType === "buyer"
//                   ? translate("bidInfo.status.notBidYet")
//                   : translate("bidInfo.status.loginAsBuyer")}
//               </span>
//             </li>
//           )}
//         {/* Prebid Timer - only show if there's actually a timer and it's not a buy now vehicle */}
//         {!isBuyNowVehicle &&
//           vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction &&
//           prebidTimeLeft > 0 && (
//             <li className={`flex justify-between ${rowClass}`}>
//               <span className='font-semibold text-primary animate-blink'>
//                 {translate("bidInfo.labels.prebidTimeLeft")}:
//               </span>
//               <span className={isRTL ? "text-left" : ""}>
//                 {(() => {
//                   if (
//                     !vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction
//                       ?.preBidEndDate
//                   )
//                     return "-";
//                   const days = String(
//                     Math.floor(prebidTimeLeft / (1000 * 60 * 60 * 24))
//                   ).padStart(2, "0");
//                   const hours = String(
//                     Math.floor(
//                       (prebidTimeLeft % (1000 * 60 * 60 * 24)) /
//                         (1000 * 60 * 60)
//                     )
//                   ).padStart(2, "0");
//                   const minutes = String(
//                     Math.floor(
//                       (prebidTimeLeft % (1000 * 60 * 60)) / (1000 * 60)
//                     )
//                   ).padStart(2, "0");
//                   const seconds = String(
//                     Math.floor((prebidTimeLeft % (1000 * 60)) / 1000)
//                   ).padStart(2, "0");
//                   return `${days} : ${hours} : ${minutes} : ${seconds}`;
//                 })()}
//               </span>
//             </li>
//           )}

//         {/* Buy Now or Bid Input Section */}
//         {isBuyNowVehicle ? (
//           /* Buy Now Section */
//           <li className='mt-2'>
//             <div className='text-center'>
//               {isFuture ||
//               buyNowTimeLeft <= 0 ||
//               isPurchaseSuccessful ||
//               isVehicleSold ? (
//                 <div className='w-full text-[14px] font-medium text-white py-2 bg-gray-400 cursor-not-allowed text-center'>
//                   {isPurchaseSuccessful
//                     ? translate("bidInfo.status.purchaseSuccessful")
//                     : isVehicleSold
//                     ? translate("bidInfo.status.vehicleSold")
//                     : buyNowTimeLeft <= 0
//                     ? translate("bidInfo.status.buyNowExpired")
//                     : translate("bidInfo.status.future")}
//                 </div>
//               ) : (
//                 <button
//                   className='w-full text-[16px] font-bold text-white py-3 bg-green-600 hover:bg-green-700 transition-colors duration-200'
//                   onClick={handleBuyNowClick}
//                   disabled={isSubmittingBid}
//                 >
//                   {isSubmittingBid
//                     ? translate("bidInfo.buttons.processing")
//                     : translate("bidInfo.buttons.buyNow")}
//                 </button>
//               )}
//             </div>
//           </li>
//         ) : (
//           /* Prebid Section */
//           handlePreBidSubmit && (
//             <li className='mt-2'>
//               {isVehicleSold ? (
//                 /* Vehicle Sold Button */
//                 <div className='w-full text-[14px] font-medium text-white py-2 bg-red-600 cursor-not-allowed text-center'>
//                   {translate("bidInfo.status.vehicleSold")}
//                 </div>
//               ) : (
//                 <>
//                   <div
//                     className={`flex items-end justify-between ${
//                       isRTL ? "flex-row-reverse" : ""
//                     }`}
//                   >
//                     <span className='font-semibold'>
//                       {translate("bidInfo.labels.makePreBid")}:
//                     </span>
//                     <div
//                       className={`flex items-center gap-1 ${
//                         isRTL ? "flex-row-reverse" : ""
//                       }`}
//                     >
//                       <button
//                         className={`px-2 py-1 ${
//                           isFuture
//                             ? "bg-gray-300 cursor-not-allowed"
//                             : "bg-[#DFDFDF] hover:bg-gray-200"
//                         }`}
//                         onClick={
//                           isFuture
//                             ? undefined
//                             : () => {
//                                 // For decrement, we'll still use the manual calculation
//                                 // since updateBidAmount always increases the bid
//                                 const currentAmount =
//                                   parseFloat(bidAmount) || 0;
//                                 // Get minimum bid amount
//                                 const startingBid =
//                                   parseFloat(vehicle?.startingBid) || 0;
//                                 const currentBid =
//                                   parseFloat(vehicle?.latestBid) || 0;
//                                 const minBidAmount =
//                                   currentBid > 0
//                                     ? currentBid + bidIncrement
//                                     : startingBid + bidIncrement;

//                                 // Calculate new amount but don't go below minimum
//                                 const newAmount = Math.max(
//                                   minBidAmount,
//                                   currentAmount - bidIncrement
//                                 );
//                                 setBidAmount(newAmount.toString());
//                               }
//                         }
//                         disabled={isFuture}
//                       >
//                         -
//                       </button>
//                       <input
//                         type='number'
//                         value={bidAmount}
//                         onChange={
//                           isFuture
//                             ? undefined
//                             : (e) => {
//                                 const newValue = e.target.value;
//                                 // Calculate minimum bid amount
//                                 const startingBid =
//                                   parseFloat(vehicle?.startingBid) || 0;
//                                 const currentBid =
//                                   parseFloat(vehicle?.latestBid) || 0;
//                                 const minBidAmount =
//                                   currentBid > 0
//                                     ? currentBid + bidIncrement
//                                     : startingBid + bidIncrement;

//                                 // Validate that the new value is not below minimum
//                                 if (
//                                   newValue === "" ||
//                                   parseFloat(newValue) >= minBidAmount
//                                 ) {
//                                   setBidAmount(newValue);
//                                 }
//                               }
//                         }
//                         className={`px-4 w-24 py-1 font-medium text-[14px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
//                           isFuture
//                             ? "bg-gray-300 cursor-not-allowed"
//                             : "bg-[#DFDFDF]"
//                         } ${isRTL ? "text-right" : ""}`}
//                         placeholder={translate(
//                           "bidInfo.labels.amountPlaceholder"
//                         )}
//                         disabled={isFuture}
//                       />
//                       <button
//                         className={`px-2 py-1 ${
//                           isFuture
//                             ? "bg-gray-300 cursor-not-allowed"
//                             : "bg-[#DFDFDF] hover:bg-gray-200"
//                         }`}
//                         onClick={
//                           isFuture
//                             ? undefined
//                             : () => {
//                                 const currentAmount =
//                                   parseFloat(bidAmount) || 0;
//                                 // Calculate minimum bid amount
//                                 const startingBid =
//                                   parseFloat(vehicle?.startingBid) || 0;
//                                 const currentBid =
//                                   parseFloat(vehicle?.latestBid) || 0;
//                                 const minBidAmount =
//                                   currentBid > 0
//                                     ? currentBid + bidIncrement
//                                     : startingBid + bidIncrement;

//                                 // Calculate new amount - ensure it's at least minimum
//                                 const newAmount = Math.max(
//                                   minBidAmount,
//                                   currentAmount + bidIncrement
//                                 );
//                                 setBidAmount(newAmount.toString());
//                               }
//                         }
//                         disabled={isFuture}
//                       >
//                         +
//                       </button>
//                     </div>
//                   </div>

//                   {isFuture ? (
//                     <div className='mt-3 w-full text-[14px] font-medium text-white py-2 bg-gray-400 cursor-not-allowed text-center'>
//                       {translate("bidInfo.status.future")}
//                     </div>
//                   ) : (
//                     <button
//                       className='mt-3 w-full text-[14px] font-medium text-white py-2 bg-primary-dark hover:bg-primary-dark/90'
//                       onClick={() => {
//                         if (!user?.id) {
//                           alert(translate("bidInfo.alerts.loginToBid"));
//                           return;
//                         }
//                         if (user?.seller?.id) {
//                           alert(translate("bidInfo.alerts.sellerCannotBid"));
//                           return;
//                         }
//                         if (!hasActivePackage) {
//                           alert(
//                             translate("bidInfo.alerts.needActivePackageBid")
//                           );
//                           return;
//                         }
//                         handlePreBidSubmit();
//                       }}
//                       disabled={isSubmittingBid}
//                     >
//                       {isSubmittingBid
//                         ? translate("bidInfo.buttons.submitting")
//                         : translate("bidInfo.buttons.prebid")}
//                     </button>
//                   )}

//                   {/* Bid Message Display */}
//                   {bidMessage && (
//                     <div
//                       className={`mt-2 p-2 rounded text-sm ${
//                         bidMessage.includes("Error")
//                           ? "bg-red-100 text-red-700 border border-red-200"
//                           : bidMessage.includes("successfully")
//                           ? "bg-green-100 text-green-700 border border-green-200"
//                           : "bg-blue-100 text-blue-700 border border-blue-200"
//                       }`}
//                     >
//                       {bidMessage}
//                     </div>
//                   )}
//                 </>
//               )}
//             </li>
//           )
//         )}
//       </ul>

//       {/* Buy Now Confirmation Modal */}
//       {showBuyNowModal && (
//         <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
//           <div
//             className='bg-white rounded-lg p-6 max-w-md w-full mx-4'
//             dir={isRTL ? "rtl" : "ltr"}
//             lang={language}
//           >
//             {/* Header */}
//             <div
//               className={`flex justify-between items-center mb-4 ${
//                 isRTL ? "flex-row-reverse" : ""
//               }`}
//             >
//               <h3 className='text-lg font-bold text-gray-900'>
//                 {translate("bidInfo.labels.buyNowConfirmation")}
//               </h3>
//               <button
//                 onClick={handleBuyNowModalClose}
//                 className='text-gray-400 hover:text-gray-600'
//               >
//                 <svg
//                   className='w-6 h-6'
//                   fill='none'
//                   stroke='currentColor'
//                   viewBox='0 0 24 24'
//                 >
//                   <path
//                     strokeLinecap='round'
//                     strokeLinejoin='round'
//                     strokeWidth={2}
//                     d='M6 18L18 6M6 6l12 12'
//                   />
//                 </svg>
//               </button>
//             </div>

//             {/* Vehicle Information */}
//             <div className='mb-6'>
//               <div
//                 className={`flex items-center mb-4 ${
//                   isRTL
//                     ? "space-x-reverse space-x-4 flex-row-reverse"
//                     : "space-x-4"
//                 }`}
//               >
//                 <img
//                   src={vehicle?.images?.[0]?.imageUrl || "/car-placeholder.png"}
//                   alt={vehicle?.name}
//                   className='w-16 h-12 object-cover rounded'
//                 />
//                 <div>
//                   <h4 className='font-semibold text-gray-900'>
//                     {vehicle?.name}
//                   </h4>
//                   <p className='text-sm text-gray-600'>
//                     Lot: {vehicle?.lotNumber}
//                   </p>
//                 </div>
//               </div>

//               <div className='bg-gray-50 p-4 rounded-lg'>
//                 <div
//                   className={`flex justify-between items-center mb-2 ${
//                     isRTL ? "flex-row-reverse" : ""
//                   }`}
//                 >
//                   <span className='text-gray-600'>
//                     {translate("bidInfo.labels.buyNowPrice")}:
//                   </span>
//                   <span className='text-xl font-bold text-green-600'>
//                     {formatPrice(vehicle?.buyNowPrice)}
//                   </span>
//                 </div>
//                 {vehicle?.buyNowEndTime && (
//                   <div
//                     className={`flex justify-between items-center ${
//                       isRTL ? "flex-row-reverse" : ""
//                     }`}
//                   >
//                     <span className='text-gray-600'>
//                       {translate("bidInfo.labels.buyNowAvailableUntil")}:
//                     </span>
//                     <span className='text-sm text-gray-800'>
//                       {new Date(vehicle.buyNowEndTime).toLocaleDateString()}
//                     </span>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Confirmation Message */}
//             <div className='mb-6'>
//               <p className='text-gray-700 text-center'>
//                 {translate("bidInfo.labels.buyNowMessagePrefix")}{" "}
//                 <span className='font-bold text-green-600'>
//                   {formatPrice(vehicle?.buyNowPrice)}
//                 </span>
//                 {translate("bidInfo.labels.buyNowMessageSuffix")}
//               </p>
//             </div>

//             {/* Action Buttons */}
//             <div
//               className={`flex ${
//                 isRTL ? "space-x-reverse space-x-3" : "space-x-3"
//               }`}
//             >
//               <button
//                 onClick={handleBuyNowModalClose}
//                 className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
//               >
//                 {translate("bidInfo.buttons.cancel")}
//               </button>
//               <button
//                 onClick={() => {
//                   setShowBuyNowModal(false);
//                   if (handleBuyNowSubmit) {
//                     handleBuyNowSubmit();
//                   }
//                 }}
//                 className='flex-1 px-4 py-2 bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white rounded-lg hover:shadow-lg transition-all duration-200 font-semibold'
//               >
//                 {translate("bidInfo.buttons.confirmPurchase")}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default BidInformation;
